import { Component, ViewChild } from '@angular/core';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import {
    ChartComponent,
    ApexNonAxisChartSeries,
    ApexResponsive,
    ApexChart,
    ApexTitleSubtitle,
    ApexFill,
    ApexStroke,
    ApexYAxis,
    ApexLegend,
    ApexGrid,
    ApexPlotOptions,
    NgApexchartsModule,
    ApexTooltip,
} from 'ng-apexcharts';
import { GlobalConstants } from '../../common/global-constants';

export type VulnerabilityChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    responsive: ApexResponsive[];
    labels: any;
    dataLabels: any;
    title: ApexTitleSubtitle;
    fill: ApexFill;
    tooltip: ApexTooltip;
    yaxis: ApexYAxis;
    stroke: ApexStroke;
    legend: ApexLegend;
    colors: string[];
    plotOptions: ApexPlotOptions;
};

export type RiskChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    responsive: ApexResponsive[];
    labels: any;
    legend: ApexLegend;
    grid: ApexGrid;
    stroke: ApexStroke;
    fill: ApexFill;
    colors: string[];
};

@Component({
    selector: 'app-i-infrastructure',
    standalone: true,
    imports: [NgApexchartsModule],
    templateUrl: './i-infrastructure.component.html',
    styleUrl: './i-infrastructure.component.scss',
})
export class IInfrastructureComponent {
    internal = GlobalConstants.internal;

    // isToggled
    isToggled = false;
    @ViewChild('vulnerabilityChart') vulnerabilityChartRef: ChartComponent;
    public vulnerabilityChartOptions: Partial<VulnerabilityChartOptions>;
    @ViewChild('riskChart') riskChartRef: ChartComponent;
    public riskChartOptions: Partial<RiskChartOptions>;

    // Opcional: Definir las constantes de datos y colores en el componente
    altairColors = {
        critical: '#B91C1C',
        high: '#D97706',
        medium: '#fbbf24',
        low: '#00C688',
        primary: '#072E48',
        text: '#686E7A',
    };

    vulnerabilityData = {
        labels: ['Críticas', 'Altas', 'Medias', 'Bajas'],
        data: [15, 30, 87, 18],
        backgroundColor: [
            this.altairColors.critical,
            this.altairColors.high,
            this.altairColors.medium,
            this.altairColors.low,
        ],
        borderWidth: 2,
        borderColor: '#ffffff',
    };

    riskData = {
        labels: [
            'Hosts Críticos',
            'Hosts con Riesgo Alto',
            'Hosts con Riesgo Medio',
            'Hosts Seguros',
        ],
        data: [12, 18, 25, 23],
        backgroundColor: [
            this.altairColors.critical + '80',
            this.altairColors.high + '80',
            this.altairColors.medium + '80',
            this.altairColors.low + '80',
        ],
        borderColor: [
            this.altairColors.critical,
            this.altairColors.high,
            this.altairColors.medium,
            this.altairColors.low,
        ],
        borderWidth: 2,
    };

    constructor(
        public themeService: CustomizerSettingsService
    ) {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });

        this.vulnerabilityChartOptions = {
            series: this.vulnerabilityData.data,
            chart: {
                height: 400,
                type: 'donut',
                toolbar: {
                    show: true,
                },
            },
            labels: this.vulnerabilityData.labels,
            colors: this.vulnerabilityData.backgroundColor,
            fill: {
                opacity: 1,
                colors: this.vulnerabilityData.backgroundColor,
            },
            stroke: {
                width: 1,
                colors: undefined,
            },
            yaxis: {
                show: false,
            },
            legend: {
                offsetY: -10,
                fontSize: '14px',
                position: 'bottom',
                horizontalAlign: 'center',
                labels: {
                    colors: '#919aa3',
                },
                itemMargin: {
                    horizontal: 12,
                    vertical: 7,
                },
                formatter: function (seriesName, opts) {
                    return (
                        seriesName +
                        ' - ' +
                        opts.w.globals.series[opts.seriesIndex]
                    );
                },
            },
            plotOptions: {
                pie: {
                    donut: {
                        labels: {
                            show: true,
                            name: {
                                show: true,
                            },
                            value: {
                                show: true,
                            },
                            total: {
                                show: true,
                                showAlways: true,
                            },
                        },
                    },
                },
            },
            tooltip: {
                y: {
                    formatter: function (val) {
                        return val + '';
                    },
                },
            },
        };

        this.riskChartOptions = {
            series: this.riskData.data,
            chart: {
                height: 400,
                type: 'polarArea',
                toolbar: {
                    show: true,
                },
            },
            colors: this.riskData.backgroundColor,
            stroke: {
                width: this.riskData.borderWidth,
                colors: this.riskData.borderColor,
            },
            fill: {
                opacity: 0.8,
                colors: this.riskData.backgroundColor,
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        chart: {
                            width: 200,
                        },
                        legend: {
                            position: 'bottom',
                        },
                    },
                },
            ],
            labels: this.riskData.labels,
            grid: {
                show: true,
                strokeDashArray: 5,
                borderColor: '#e0e0e0',
            },
            legend: {
                position: 'bottom',
                fontSize: '14px',
                labels: {
                    colors: '#919aa3',
                },
                itemMargin: {
                    horizontal: 10,
                    vertical: 0,
                },
            },
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for role name
     */
    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    /**
     * Getter for company name
     */
    get companyName(): string {
        return localStorage.getItem('companyName') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
}
